/**
 * Notification Preferences
 *
 * Default preferences and configuration for the notification system.
 */

import type { PreferenceKey, UserControllableChannel } from "./channels";
import { NotificationChannels, PreferenceKeys } from "./channels";
import type { NotificationPriorityType, NotificationType } from "./types";
import { NotificationPriority, NotificationTypes } from "./types";

// ============================================================================
// Default Preferences
// ============================================================================

/**
 * Default external channel preferences for each preference key
 * Note: IN_APP is always enabled (industry standard) and not included here
 * These are only the user-controllable external delivery channels
 */
export const defaultPreferences: Record<
  PreferenceKey,
  UserControllableChannel[]
> = {
  // Surveys - email by default
  [PreferenceKeys.SURVEY_SENT]: [NotificationChannels.EMAIL],

  // Referral outreach - email by default (SMS opt-in)
  [PreferenceKeys.REFERRAL_OUTREACH]: [NotificationChannels.EMAIL],

  // New opportunity alerts - OFF by default; explicit opt-in only.
  // Empty default is what makes resolveEnabledChannels treat this as off
  // until the user turns it on.
  [PreferenceKeys.NEW_OPPORTUNITY_ALERTS]: [],

  // Financial - always email for paper trail
  [PreferenceKeys.EARNINGS_PAYOUT]: [NotificationChannels.EMAIL],
  [PreferenceKeys.BALANCE_ALERTS]: [NotificationChannels.EMAIL],

  // Organization - email only (no push - researcher-facing)
  [PreferenceKeys.ORG_INVITATIONS]: [NotificationChannels.EMAIL],
  [PreferenceKeys.ORG_UPDATES]: [NotificationChannels.EMAIL],

  // Study management - email only
  [PreferenceKeys.BUDGET_THRESHOLD]: [NotificationChannels.EMAIL],
  [PreferenceKeys.STUDY_MILESTONES]: [NotificationChannels.EMAIL],
  [PreferenceKeys.STUDY_APPROVALS]: [NotificationChannels.EMAIL],
};

// ============================================================================
// Priority Configuration
// ============================================================================

/**
 * Default priority for each notification type
 */
export const notificationTypePriority: Record<
  NotificationType,
  NotificationPriorityType
> = {
  // Organization - normal priority
  [NotificationTypes.ORGANIZATION_INVITATION_CREATED]:
    NotificationPriority.NORMAL,
  [NotificationTypes.ORGANIZATION_INVITATION_ACCEPTED]:
    NotificationPriority.LOW,
  [NotificationTypes.ORGANIZATION_INVITATION_REVOKED]: NotificationPriority.LOW,

  // Surveys - normal to high
  [NotificationTypes.INTERVIEW_INVITATION_SENT]: NotificationPriority.HIGH,

  // Financial - high to critical
  [NotificationTypes.INTERVIEW_EARNINGS_POSTED]: NotificationPriority.HIGH,
  [NotificationTypes.INTERVIEW_APPROVED]: NotificationPriority.HIGH,
  [NotificationTypes.INTERVIEW_AUTO_APPROVED]: NotificationPriority.NORMAL,
  [NotificationTypes.INTERVIEW_REJECTED]: NotificationPriority.HIGH,
  [NotificationTypes.PAYMENT_CLAWED_BACK]: NotificationPriority.CRITICAL,
  [NotificationTypes.BALANCE_NEGATIVE]: NotificationPriority.CRITICAL,

  // Study management - normal to high
  [NotificationTypes.BUDGET_THRESHOLD_REACHED]: NotificationPriority.HIGH,
  [NotificationTypes.STUDY_APPROVAL_REQUESTED]: NotificationPriority.HIGH,
  [NotificationTypes.STUDY_APPROVAL_DECIDED]: NotificationPriority.NORMAL,
  [NotificationTypes.STUDY_STARTED]: NotificationPriority.NORMAL,
  [NotificationTypes.STUDY_COMPLETED]: NotificationPriority.LOW,
  [NotificationTypes.STUDY_ETA_SET]: NotificationPriority.HIGH,
  [NotificationTypes.STUDY_ETA_UPDATED]: NotificationPriority.NORMAL,
  [NotificationTypes.FEASIBILITY_QUOTE_READY]: NotificationPriority.HIGH,

  // Panel engagement - normal priority
  [NotificationTypes.INVITATION_REMINDER]: NotificationPriority.NORMAL,
  [NotificationTypes.INVITATION_REOPENED]: NotificationPriority.HIGH,
  [NotificationTypes.POST_FIRST_INTERVIEW]: NotificationPriority.NORMAL,
  [NotificationTypes.INVITATION_FEEDBACK_REQUEST]: NotificationPriority.LOW,
  [NotificationTypes.POWER_USER_NPS]: NotificationPriority.LOW,
  // Study health alerts (researcher)
  [NotificationTypes.STUDY_STUCK]: NotificationPriority.HIGH,
  [NotificationTypes.STUDY_FULFILLMENT_ALERT]: NotificationPriority.HIGH,
  [NotificationTypes.STUDY_BOOKING_NEEDED]: NotificationPriority.NORMAL,
  [NotificationTypes.RESEARCHER_REVIEW_DIGEST]: NotificationPriority.NORMAL,
  [NotificationTypes.INTERVIEW_BOOKED]: NotificationPriority.HIGH,
  [NotificationTypes.INTERVIEW_REMINDER]: NotificationPriority.HIGH,
  [NotificationTypes.STUDY_EMAIL_BLAST]: NotificationPriority.NORMAL,
  [NotificationTypes.CONTRACT_SIGNED]: NotificationPriority.HIGH,
  // Not HIGH: an announcement of a schedule, delivered by an hourly sweep on
  // purpose. The calendar entry it carries raises its own alarm.
  [NotificationTypes.OFFICE_HOURS_ANNOUNCED]: NotificationPriority.NORMAL,
  [NotificationTypes.PROJECT_OFFBOARDED]: NotificationPriority.HIGH,
  [NotificationTypes.PROJECT_CLOSED]: NotificationPriority.HIGH,
  // Support
  [NotificationTypes.SUPPORT_AUTO_RESPONSE]: NotificationPriority.LOW,

  // System
  [NotificationTypes.WELCOME]: NotificationPriority.NORMAL,
  // Referral outreach
  [NotificationTypes.REFERRAL_OUTREACH_REQUEST]: NotificationPriority.NORMAL,
  [NotificationTypes.POST_COMPLETION_REFERRAL]: NotificationPriority.NORMAL,
  // Multi-task studies
  [NotificationTypes.NEXT_TASK_AVAILABLE]: NotificationPriority.HIGH,
  [NotificationTypes.TASK_APPROVED]: NotificationPriority.HIGH,

  // Lifecycle emails
  [NotificationTypes.WELCOME_NUDGE_FIRST]: NotificationPriority.LOW,
  [NotificationTypes.WELCOME_NUDGE_LAST]: NotificationPriority.LOW,
  [NotificationTypes.FINISH_PROFILE_FIRST]: NotificationPriority.NORMAL,
  [NotificationTypes.FINISH_PROFILE_LAST]: NotificationPriority.LOW,
  [NotificationTypes.ONBOARDING_CONFIRMED]: NotificationPriority.NORMAL,
  [NotificationTypes.APPROVED_NOT_STARTED_FIRST]: NotificationPriority.HIGH,
  [NotificationTypes.APPROVED_NOT_STARTED_LAST]: NotificationPriority.HIGH,
  [NotificationTypes.APPLIED_SCREENED_OUT]: NotificationPriority.LOW,
  [NotificationTypes.APPLIED_NOT_SELECTED]: NotificationPriority.LOW,
  [NotificationTypes.DORMANT_30_DAYS]: NotificationPriority.LOW,
  [NotificationTypes.DORMANT_60_DAYS]: NotificationPriority.LOW,
  [NotificationTypes.DORMANT_90_DAYS]: NotificationPriority.LOW,
  [NotificationTypes.COMPLETE_MATCH_PROFILE]: NotificationPriority.NORMAL,
  // Referrer alerts
  [NotificationTypes.OPPORTUNITY_LISTED]: NotificationPriority.NORMAL,
  // First-opportunity onboarding emails
  [NotificationTypes.FIRST_OPPORTUNITY_AWAITING_REVIEW]:
    NotificationPriority.HIGH,
  [NotificationTypes.FIRST_OPPORTUNITY_SCREEN_PASSED]:
    NotificationPriority.HIGH,
  [NotificationTypes.OPPORTUNITY_SCREEN_PASSED]: NotificationPriority.HIGH,
  [NotificationTypes.SCREENING_STEP_READY]: NotificationPriority.HIGH,
  // Network connections
  [NotificationTypes.CONNECTION_MATCH]: NotificationPriority.NORMAL,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get default external channels for a preference key
 * Note: IN_APP is always enabled and not included in these defaults
 */
export function getDefaultChannels(
  key: PreferenceKey,
): UserControllableChannel[] {
  return defaultPreferences[key];
}

/**
 * Get priority for a notification type
 */
export function getPriorityForType(
  type: NotificationType,
): NotificationPriorityType {
  return notificationTypePriority[type];
}

/**
 * Check if a notification type is critical
 */
export function isCriticalNotification(type: NotificationType): boolean {
  return getPriorityForType(type) === NotificationPriority.CRITICAL;
}
